/**
 * @module app/router
 * @description Minimal hash router: #/ (browse), #/checkout, #/payment, #/thank-you,
 * and #/stage/<causeId> (Immersive).
 *
 * Hash routing works on any static host (Pages, a WordPress embed) with no
 * server rewrites, and the browser Back button works between steps.
 */
import { useEffect, useState } from "react";

/** Every screen in the giving journey. */
export type Route = "browse" | "checkout" | "payment" | "thank-you" | "stage";

const ROUTES: readonly Route[] = ["browse", "checkout", "payment", "thank-you", "stage"];

/** Hash path segments: "#/stage/cove-primary-school" → ["stage", "cove-primary-school"]. */
function segments(): string[] {
    return window.location.hash.replace(/^#\/?/, "").split("/").filter((s: string) => s !== "");
}

/** @returns the Route for the current hash (unknown → browse). */
function readRoute(): Route {
    const slug: string = segments()[0] ?? "";
    return (ROUTES as readonly string[]).includes(slug) ? (slug as Route) : "browse";
}

/** @returns the route parameter (e.g. the stage's cause id), or null. */
export function routeParam(): string | null {
    return segments()[1] ?? null;
}

/** Navigates to a route (with an optional parameter) and scrolls to the top of the new screen. */
export function navigate(route: Route, param?: string): void {
    const path: string = route === "browse" ? "/" : `/${route}${param ? `/${encodeURIComponent(param)}` : ""}`;
    window.location.hash = path;
    window.scrollTo({ top: 0 });
}

/** @returns the current route, re-rendering on hash changes. */
export function useRoute(): Route {
    const [route, setRoute] = useState<Route>(readRoute);
    const [, setParam] = useState<string | null>(routeParam);
    useEffect(() => {
        // Track the parameter too, so moving stage → stage re-renders.
        const onChange = (): void => { setRoute(readRoute()); setParam(routeParam()); };
        window.addEventListener("hashchange", onChange);
        return () => window.removeEventListener("hashchange", onChange);
    }, []);
    return route;
}
